import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "./Services.css";

const services = [
  {
    id: 1,
    slug: "firmware",
    icon: "⚙️",
    title: "Firmware Development",
    desc: "We develop robust firmware for embedded devices, ensuring reliability, performance, and seamless integration across platforms and applications.",
    pills: ["System Architecture", "Electronic Design", "RTOS / Bare Metal", "Porting & Optimization", "BSP / Device Drivers"],
    accent: "#065f46",
  },
  {
    id: 2,
    slug: "hardware",
    icon: "🔌",
    title: "Hardware & Electrical Design",
    desc: "Comprehensive hardware services covering every stage of development, from concept to production-ready boards.",
    pills: ["High-Speed Digital", "Analog Circuit", "Power Electronics", "Low Power Design", "Signal Integrity"],
    accent: "#065f46",
  },
  {
    id: 3,
    slug: "pcb",
    icon: "🖥️",
    title: "PCB Layout Design",
    desc: "End-to-end PCB layout services for all electronic product types and complexities.",
    pills: ["Multilayer PCB", "DFM / DFT / DFA", "Thermal Analysis"],
    accent: "#065f46",
  },
  {
    id: 4,
    slug: "software",
    icon: "💻",
    title: "Software Design",
    desc: "Full-cycle software development for embedded and application systems.",
    pills: ["Planning", "Architecture", "Development", "QA Testing", "Deployment"],
    accent: "#065f46",
  },
  {
    id: 5,
    slug: "mechanical",
    icon: "🔩",
    title: "Mechanical Design",
    desc: "Functional mechanical systems using engineering principles and CAD software.",
    pills: ["2D / 3D Design", "Simulation", "Prototype", "Mold Design"],
    accent: "#065f46",
  },
];

const Services = () => {
  const navigate = useNavigate();
  
  const goToEmbedded = (serviceSlug) => {
    navigate(`/embedded?service=${serviceSlug}`);
  };

  /* Heading animation */
  const fadeUp = {
    hidden: { opacity: 0, y: -22 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };
  const ornAnim = {
    hidden: { opacity: 0, scaleX: 0 },
    visible: { opacity: 1, scaleX: 1, transition: { duration: 0.7, delay: 0.35, ease: "easeOut" } },
  };

  return (
    <section className="serv-section">
      <div className="serv-container">

        {/* ── HEADING ── */}
        <motion.div
          className="serv-heading-wrap"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <motion.span className="serv-heading-serif" variants={fadeUp}>
            
          </motion.span>
          <motion.h2 className="serv-heading" variants={fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
            Our <span>Services</span>
          </motion.h2>
          <motion.div className="serv-heading-ornament" variants={ornAnim}>
            <div className="serv-orn-line-l" />
            <div className="serv-orn-gem" />
            <div className="serv-orn-line-r" />
          </motion.div>
        </motion.div>

        {/* ── SUBTITLE (clickable) ── */}
        <motion.div
          className="serv-subtitle-wrap"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div
            className="serv-subtitle"
            onClick={() => goToEmbedded("firmware")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && goToEmbedded("firmware")}
          >
            Embedded Design Services
          </div>
        </motion.div>

        {/* ── BENTO GRID ── */}
        <div className="serv-bento">
          {services.map((s, i) => (
            <motion.div
              key={s.id}
              className="serv-card"
              style={{ "--accent": s.accent }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: i * 0.1, ease: "easeOut" }}
              onClick={() => goToEmbedded(s.slug)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && goToEmbedded(s.slug)}
              style={{
                "--accent": s.accent,
                cursor: "pointer",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
              }}
              whileHover={{ y: -8, boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)" }}
            >
              {/* Icon + Title */}
              <div className="serv-icon-row">
                <div className="serv-icon-box">{s.icon}</div>
                <h3 className="serv-card-title">
                  {s.title}
                </h3>
              </div>

              {/* Description */}
              <p className="serv-card-desc">{s.desc}</p>

              {/* Divider */}
              <div className="serv-divider" />

              {/* CTA */}
              <div className="serv-cta">
                <span className="serv-cta-text">Learn More</span>
                <span className="serv-cta-arrow">→</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── EXPLORE BUTTON ── */}
        <motion.div
          className="serv-btn-wrap"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
         
        </motion.div>

      </div>
    </section>
  );
};

export default Services;